import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { TooltipModule } from "primeng/tooltip";
import { ScheduledOrder } from "../../../../core/models/scheduled-order.interface";

/**
 * Quick Actions Bar Component
 * Provides one-tap access to frequently-used scheduled order actions
 * Designed for mobile-first with proper touch targets (48px minimum)
 *
 * Features:
 * - Status-aware action buttons (Pause/Activate based on order status)
 * - Color-coded buttons by severity (success/warning/info/danger)
 * - Accessible with WCAG AA compliance
 * - Reusable across list view and edit page contexts
 *
 * Usage:
 * ```html
 * <app-scheduled-order-quick-actions
 *   [order]="scheduledOrder"
 *   [displayMode]="'horizontal'"
 *   (actionClick)="handleAction($event)"
 * />
 * ```
 */
@Component({
  selector: "app-scheduled-order-quick-actions",
  standalone: true,
  imports: [CommonModule, ButtonModule, TooltipModule],
  templateUrl: "./scheduled-order-quick-actions.component.html",
  styleUrl: "./scheduled-order-quick-actions.component.scss",
})
export class ScheduledOrderQuickActionsComponent {
  /**
   * The scheduled order to show actions for
   */
  @Input() order!: ScheduledOrder;

  /**
   * Display mode: horizontal (default) or vertical
   * Horizontal: side-by-side buttons (good for mobile cards)
   * Vertical: stacked buttons (good for sidebar or narrow spaces)
   */
  @Input() displayMode: "horizontal" | "vertical" = "horizontal";

  /**
   * Whether to show the Process Now button
   * Default: true
   */
  @Input() showProcessNowButton = true;

  /**
   * Compact mode (smaller buttons, icons only)
   * Default: false
   */
  @Input() compact = false;

  /**
   * Loading state for async operations
   */
  @Input() loading = false;

  /**
   * Filter-style mode: uses blue filter-button styling
   * Matches the filter buttons appearance (light border, blue color)
   */
  @Input() filterStyle = false;

  /**
   * Show Edit button (for list view only, not in card expanded view)
   */
  @Input() showEditButton = false;

  /**
   * Emits when an action button is clicked
   * Action types: 'pause' | 'activate' | 'processNow' | 'retry' | 'reschedule' | 'delete'
   */
  @Output() actionClick = new EventEmitter<{
    action: string;
    order: ScheduledOrder;
  }>();

  /**
   * Check if Pause button should be shown
   * Only for Active orders
   */
  shouldShowPause(): boolean {
    return this.order?.Status === "Active";
  }

  /**
   * Check if Activate button should be shown
   * For Failed, Paused, Deleted, or Queued orders
   */
  shouldShowActivate(): boolean {
    const status = this.order?.Status;
    return (
      status === "Failed" ||
      status === "Paused" ||
      status === "Deleted" ||
      status === "Queued"
    );
  }

  /**
   * Check if Retry button should be shown
   * Only for Failed orders
   */
  shouldShowRetry(): boolean {
    return this.order?.Status === "Failed";
  }

  /**
   * Check if Process Now button should be enabled
   * For Active, Paused, or Failed orders
   */
  isProcessNowEnabled(): boolean {
    const status = this.order?.Status;
    return status === "Active" || status === "Paused" || status === "Failed";
  }

  /**
   * Handle Pause action
   */
  onPause(): void {
    if (this.loading || !this.shouldShowPause()) return;
    this.actionClick.emit({ action: "pause", order: this.order });
  }

  /**
   * Handle Activate action
   */
  onActivate(): void {
    if (this.loading || !this.shouldShowActivate()) return;
    this.actionClick.emit({ action: "activate", order: this.order });
  }

  /**
   * Handle Process Now action
   */
  onProcessNow(): void {
    if (this.loading || !this.isProcessNowEnabled()) return;
    this.actionClick.emit({ action: "processNow", order: this.order });
  }

  /**
   * Handle Retry action (for Failed orders)
   */
  onRetry(): void {
    if (this.loading || !this.shouldShowRetry()) return;
    this.actionClick.emit({ action: "retry", order: this.order });
  }

  /**
   * Check if Reschedule button should be shown
   * Only for Active, Failed, or Paused orders
   */
  shouldShowReschedule(): boolean {
    const status = this.order?.Status;
    return status === "Active" || status === "Failed" || status === "Paused";
  }

  /**
   * Handle Reschedule action
   */
  onReschedule(): void {
    if (this.loading || !this.shouldShowReschedule()) return;
    this.actionClick.emit({ action: "reschedule", order: this.order });
  }

  /**
   * Check if Cancel/Delete button should be shown
   * Show for Active, Failed, and Paused orders only
   * Not for: Processing, Deleted, or Queued
   */
  shouldShowDelete(): boolean {
    const status = this.order?.Status;
    return status === "Active" || status === "Failed" || status === "Paused";
  }

  /**
   * Handle Cancel/Delete action
   */
  onDelete(): void {
    if (this.loading || !this.shouldShowDelete()) return;
    this.actionClick.emit({ action: "delete", order: this.order });
  }

  /**
   * Handle Edit action (for list view only)
   */
  onEdit(): void {
    if (this.loading) return;
    this.actionClick.emit({ action: "edit", order: this.order });
  }

  /**
   * Get button label for status-based action
   * Returns either "Pause" or "Activate" based on current status
   */
  getStatusActionLabel(): string {
    if (this.shouldShowPause()) return "Pause";
    if (this.shouldShowActivate()) return "Activate";
    return "";
  }

  /**
   * Get button icon for status-based action
   */
  getStatusActionIcon(): string {
    if (this.shouldShowPause()) return "pi pi-pause";
    if (this.shouldShowActivate()) return "pi pi-play";
    return "";
  }

  /**
   * Get button severity for status-based action
   */
  getStatusActionSeverity(): "secondary" | "success" {
    if (this.shouldShowPause()) return "secondary";
    return "success";
  }
}
